import { snake } from 'change-case'
import { HumanInfoMessages, HumanErrorMessages } from '../../constants'
import { api } from '../../libs/api'
import { MySet as Set } from '../../libs/polyfill'
import pagination from './pagination'

export default {
  namespaced: true,

  modules: {
    pagination: pagination(),
  },

  state: {
    index: [],
    pageIds: [],
    checkedIds: new Set([]),
    pageCheckedIds: new Set([]),
    detail: {},
    comment: '',
    showAiModal: false,
    // 直近の fetchIndex / fetchGroupingIndex のエラー（成功時は null）。
    // コンポーネント側 avoidBlank() で「API エラー時は page-- しない」判定に使う。
    lastFetchError: null,
  },
  getters: {
    favoredIds: state => new Set(state.index.map(({ id }) => id)),
    favored: (state, getters) => id => getters.favoredIds.has(id),
    markedIds: state => new Set(state.index.map(({ companyId }) => companyId)),
    marked: (state, getters) => companyId => getters.markIds.has(companyId),
    checkedCount: state => state.checkedIds.size,
    checkedIds: state => state.checkedIds,
    checked: (_, getters) => getters.checkedCount > 0,
    // eslint-disable-next-line
    pageAllChecked: (state, getters) => getters.checkedCount
      ? !state.pageIds.find(n => !state.pageCheckedIds.has(n))
      : false,
    showAi: state => state.showAiModal,
  },
  mutations: {
    comment: (state, value) => state.comment = value,
    updater: (state, value) => state.detail.comment_updater = value.updater,
    index: (state, index) => {
      const pageIds = index.map(({ id }) => id)
      const pageCheckedIds = pageIds.filter(id => state.checkedIds.has(id))

      const newIndex = index.map((job) => {
        const newJob = Object.assign({}, job)
        newJob.favored = job.in_job_list
        newJob.marked = job.in_job_mark
        newJob.job_mark_user = job.job_mark_user
        const innerHits = job.inner_hits || []
        newJob.inner_hits = innerHits.map((innerJob) => {
          const newInnerJob = Object.assign({}, innerJob)
          newInnerJob.favored = innerJob.in_job_list
          newInnerJob.marked = innerJob.in_job_mark
          newInnerJob.job_mark_user = innerJob.job_mark_user
          return newInnerJob
        })
        return newJob
      })
      state.index = newIndex
      state.pageIds = pageIds
      state.pageCheckedIds = new Set(pageCheckedIds)
    },
    check: (state, id) => {
      const newCheckedIds = new Set(state.checkedIds)
      const newPageCheckedIds = new Set(state.pageCheckedIds)
      if (Array.isArray(id)) {
        id.forEach((i) => {
          newCheckedIds.add(i)
          newPageCheckedIds.add(i)
        })
      } else {
        newCheckedIds.add(id)
        newPageCheckedIds.add(id)
      }
      state.checkedIds = newCheckedIds
      state.pageCheckedIds = newPageCheckedIds
    },
    uncheck: (state, id) => {
      const newCheckedIds = new Set(state.checkedIds)
      const newPageCheckedIds = new Set(state.pageCheckedIds)
      if (Array.isArray(id)) {
        id.forEach((i) => {
          newCheckedIds.delete(i)
          newPageCheckedIds.delete(i)
        })
      } else {
        newCheckedIds.delete(id)
        newPageCheckedIds.delete(id)
      }

      state.checkedIds = newCheckedIds
      state.pageCheckedIds = newPageCheckedIds
    },
    uncheckAll: (state) => {
      state.checkedIds.clear()
      state.pageCheckedIds.clear()

      state.checkedIds = new Set(state.checkedIds)
      state.pageCheckedIds = new Set(state.pageCheckedIds)
    },
    clearDetail: state => state.detail = {},
    detail: (state, detail) => {
      const newJobDetail = Object.assign({}, detail)
      newJobDetail.favored = detail.in_job_list
      newJobDetail.marked = detail.in_job_mark
      state.detail = newJobDetail
    },
    favor: (state, { ids, newFavored }) => {
      let newIndex = state.index
      let jobIds = ids
      if (!Array.isArray(jobIds)) {
        jobIds = [jobIds]
      }
      jobIds.forEach((jobId) => {
        newIndex = newIndex.map((job) => {
          const newJob = Object.assign({}, job)
          if (job.id === jobId) {
            newJob.favored = newFavored
            return newJob
          }

          let changedInnerHits = false
          const innerHits = job.inner_hits || []
          newJob.inner_hits = innerHits.map((innerJob) => {
            if (innerJob.id === jobId) {
              const newInnerJob = Object.assign({}, innerJob)
              newInnerJob.favored = newFavored
              changedInnerHits = true
              return newInnerJob
            }
            return innerJob
          })

          if (changedInnerHits) {
            return newJob
          }


          return job
        })
      })
      state.index = newIndex
    },
    mark: (state, { ids, newMarked, markUser }) => {
      let newIndex = state.index
      let companyIds = ids
      if (!Array.isArray(companyIds)) {
        companyIds = [companyIds]
      }
      companyIds.forEach((companyId) => {
        newIndex = newIndex.map((job) => {
          if (job.company_id === companyId) {
            const newJob = Object.assign({}, job)
            newJob.marked = newMarked
            newMarked
            ? newJob.job_mark_user = markUser
            : newJob.job_mark_user = ''
            return newJob
          }
          return job
        })
      })
      state.index = newIndex

      if (state.detail) {
        state.detail.job_mark_user = markUser
        state.detail.marked = newMarked
      }
    },
    copyCheckedIds: (state, ids) => {
      state.checkedIds = new Set(ids)
    },
    updateJobList: (state, jobList) => {
      state.detail.job_list = jobList
    },
    hide: (state, id) => {
      let newIndex = state.index
      newIndex = newIndex.map((job) => {
        if (job.id === id) {
          const newJob = Object.assign({}, job)
          newJob.hidden = true
          return newJob
        }
        return job
      })
      state.index = newIndex
    },
    showAiModal: state => state.showAiModal = true,
    hideAiModal: state => state.showAiModal = false,
    lastFetchError: (state, error) => state.lastFetchError = error,
  },
  actions: {
    toggleCheck ({ commit, dispatch, rootGetters, state: { checkedIds } }, { checked, id }) {
      const limit = rootGetters['user/jobCsvDownloadLimit']
      if (checked && checkedIds.size >= limit) {
        dispatch(
          'common/pushToastError',
          { error: new Error(`${limit}件を超えてチェックすることはできません。`) },
          { root: true },
        )
        return
      }

      checked
        ? commit('check', id)
        : commit('uncheck', id)
    },
    togglePageCheck (
      { commit, dispatch, getters: { pageAllChecked }, rootGetters, state: { pageIds, checkedIds } },
      { event }) {
      if (!pageAllChecked) {
        const nextCheckedIds = new Set(checkedIds)
        pageIds.forEach((i) => { nextCheckedIds.add(i) })
        const limit = rootGetters['user/jobCsvDownloadLimit']
        if (nextCheckedIds.size > limit) {
          dispatch(
            'common/pushToastError',
            { error: new Error(`${limit}件を超えてチェックすることはできません。`) },
            { root: true },
          )
          event.preventDefault()
          return
        }
      }
      pageAllChecked
        ? commit('uncheck', pageIds)
        : commit('check', pageIds)
    },
    downloadJobsCsv ({ state: { checkedIds } }, { allColumns }) {
      if (checkedIds.size === 0) {
        return
      }
      return api.downloadJobsCsv(checkedIds, allColumns)
    },
    downloadGroupingJobsCsv ({ state: { checkedIds } }, { allColumns }) {
      if (checkedIds.size === 0) {
        return
      }
      return api.downloadGroupingJobsCsv(checkedIds, allColumns)
    },
    getAiManuscript ({ state: { checkedIds } }) {
      if (checkedIds.size === 0) {
        return
      }
      return api.getAiManuscript(checkedIds)
    },
    updateFavor ({ commit }, { detail, newFavored }) {
      const newJobDetail = Object.assign({}, detail)
      newJobDetail.in_job_list = newFavored
      commit('detail', newJobDetail)
    },
    async fetchIndex ({ commit, dispatch }, query) {
      try {
        const requestParameters = Object.keys(query).reduce((acc, key) => {
          acc[snake(key)] = query[key]
          return acc
        }, {})
        const { meta, index } = await api.fetchJobs(requestParameters)
        commit('pagination/newState', meta)
        commit('index', index)
        commit('lastFetchError', null)
      } catch (e) {
        commit('lastFetchError', e)
        const message = (Array.isArray(e) && e[0] && e[0].message) || HumanErrorMessages.SEARCH_FAILED
        dispatch(
          'common/pushToastError',
          { error: { message } },
          { root: true },
        )
        return Promise.reject(e)
      }
    },
    async fetchGroupingIndex ({ commit, dispatch }, query) {
      try {
        const requestParameters = Object.keys(query).reduce((acc, key) => {
          acc[snake(key)] = query[key]
          return acc
        }, {})
        const { meta, index } = await api.fetchGroupingJobs(requestParameters)
        commit('pagination/newState', meta)
        commit('index', index)
        commit('lastFetchError', null)
      } catch (e) {
        commit('lastFetchError', e)
        const message = (Array.isArray(e) && e[0] && e[0].message) || HumanErrorMessages.SEARCH_FAILED
        dispatch(
          'common/pushToastError',
          { error: { message } },
          { root: true },
        )
        return Promise.reject(e)
      }
    },
    async fetchDetail ({ commit }, { id }) {
      try {
        const response = await api.fetchJob(id)
        commit('detail', response[0])
        commit('comment', response[0].comment)
      } catch (e) {
        return Promise.reject(e)
      }
    },
    async fetchGroupingDetail ({ commit }, { id }) {
      try {
        const response = await api.fetchGroupingJob(id)
        commit('detail', response[0])
        commit('comment', response[0].comment)
      } catch (e) {
        return Promise.reject(e)
      }
    },
    async add ({ commit }, { id, ids }) {
      commit('uncheck', ids || id)
    },
    async remove ({ commit }, { id, ids }) {
      commit('uncheck', ids || id)
    },
    async removeCheckedJobs ({ commit }) {
      commit('uncheckAll')
    },
    async hideCompany ({ commit, dispatch }, { id, companyName }) {
      try {
        await api.addExclusionCompanyName({ companyName })
        commit('hide', id)
        dispatch(
          'common/pushToastInfo',
          { info: HumanInfoMessages.ADD_EXCLUSION_COMPANY_NAME },
          { root: true },
        )
      } catch (e) {
        return Promise.reject(e)
      }
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/job.js